<template>
  <div class="google-country-map">
    <div ref="chart" class="google-country-map__canvas"></div>
    <div v-if="isEmpty" class="google-country-map__empty">
      当前月份暂无国家地区数据
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts/core";
import {
  GeoComponent,
  TooltipComponent,
  VisualMapComponent,
} from "echarts/components";
import { LinesChart, MapChart, ScatterChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import worldGeoJson from "./map-data/world.geo.json";

echarts.use([
  GeoComponent,
  TooltipComponent,
  VisualMapComponent,
  LinesChart,
  MapChart,
  ScatterChart,
  CanvasRenderer,
]);

const MAP_NAME = "google-world";
let mapRegistered = false;

const COUNTRY_NAME_MAP = {
  Czechia: "Czech Republic",
  Singapore: "Singapore",
  Tanzania: "United Republic of Tanzania",
  "United States": "United States of America",
};

const COUNTRY_COORDS = {
  Austria: [14.55, 47.52],
  Canada: [-106.35, 56.13],
  China: [104.2, 35.86],
  Egypt: [30.8, 26.82],
  France: [2.21, 46.23],
  Ireland: [-8.24, 53.41],
  Japan: [138.25, 36.2],
  Lithuania: [23.88, 55.17],
  Nigeria: [8.68, 9.08],
  Philippines: [121.77, 12.88],
  Singapore: [103.82, 1.35],
  Taiwan: [120.96, 23.7],
  "United States": [-95.71, 37.09],
  "United States of America": [-95.71, 37.09],
  Vietnam: [108.28, 14.06],
};

const SMALL_COUNTRY_LABEL_OFFSETS = {
  Austria: [7, 4],
  Ireland: [-10, 5],
  Lithuania: [9, 6],
  Philippines: [12, -5],
  Singapore: [16, -8],
  Taiwan: [12, 5],
};

const businessWorldGeoJson = {
  ...worldGeoJson,
  features: (worldGeoJson.features || []).filter(
    (feature) => feature.properties?.name !== "Antarctica"
  ),
};

export default {
  name: "GoogleCountryMap",
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    height: {
      type: Number,
      default: 330,
    },
  },
  data() {
    return {
      chart: null,
    };
  },
  computed: {
    isEmpty() {
      return !this.rows.some((item) => Number(item.sessions || 0));
    },
    maxSessions() {
      return Math.max(
        1,
        ...this.rows.map((item) => Number(item.sessions || 0))
      );
    },
  },
  watch: {
    rows: {
      deep: true,
      handler() {
        this.renderChart();
      },
    },
  },
  mounted() {
    if (!mapRegistered) {
      echarts.registerMap(MAP_NAME, businessWorldGeoJson);
      mapRegistered = true;
    }
    this.$refs.chart.style.height = `${this.height}px`;
    this.chart = echarts.init(this.$refs.chart);
    this.renderChart();
    window.addEventListener("resize", this.resizeChart);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeChart);
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
  methods: {
    resizeChart() {
      if (this.chart) {
        this.chart.resize();
      }
    },
    mapCountryName(name) {
      return COUNTRY_NAME_MAP[name] || name || "-";
    },
    countryColor(value) {
      const number = Number(value || 0);
      if (number >= 1000) return "#ffd666";
      if (number >= 50) return "#1677ff";
      if (number > 0) return "#b7dcff";
      return "#eef3f9";
    },
    markerRows() {
      return this.rows
        .filter((item) => Number(item.sessions || 0) > 0)
        .map((item) => {
          const mapName = this.mapCountryName(item.country);
          const coord = COUNTRY_COORDS[item.country] || COUNTRY_COORDS[mapName];
          if (!coord) return null;
          const sessions = Number(item.sessions || 0);
          return {
            name: item.country,
            value: [...coord, sessions],
            sessions,
            active_users: Number(item.active_users || 0),
            itemStyle: {
              color: this.countryColor(sessions),
            },
          };
        })
        .filter(Boolean);
    },
    smallCountryLabelRows() {
      return this.markerRows()
        .map((item) => {
          const offset = SMALL_COUNTRY_LABEL_OFFSETS[item.name];
          if (!offset) return null;
          const coord = item.value.slice(0, 2);
          const labelCoord = [coord[0] + offset[0], coord[1] + offset[1]];
          return {
            ...item,
            value: [...labelCoord, item.sessions],
            sourceCoord: coord,
            targetCoord: labelCoord,
          };
        })
        .filter(Boolean);
    },
    smallCountryPointRows() {
      const smallNames = new Set(Object.keys(SMALL_COUNTRY_LABEL_OFFSETS));
      return this.markerRows().filter((item) => smallNames.has(item.name));
    },
    normalMarkerRows() {
      const smallNames = new Set(Object.keys(SMALL_COUNTRY_LABEL_OFFSETS));
      return this.markerRows().filter((item) => !smallNames.has(item.name));
    },
    leaderLineRows() {
      return this.smallCountryLabelRows().map((item) => ({
        name: item.name,
        coords: [item.sourceCoord, item.targetCoord],
        lineStyle: {
          color: this.countryColor(item.sessions),
        },
      }));
    },
    renderChart() {
      if (!this.chart) return;
      this.chart.setOption({
        tooltip: {
          trigger: "item",
          confine: true,
          formatter: (params) => {
            const row = params.data || {};
            const rawValue = Array.isArray(row.value)
              ? row.value[2]
              : row.value;
            const sessions = Number(rawValue || 0).toLocaleString();
            const users = Number(row.active_users || 0).toLocaleString();
            return `${params.name}<br/>访问数：${sessions}<br/>访客数：${users}`;
          },
        },
        visualMap: {
          type: "piecewise",
          left: 12,
          bottom: 8,
          itemWidth: 14,
          itemHeight: 12,
          itemGap: 8,
          orient: "vertical",
          pieces: [
            { min: 1000, label: "1000以上", color: "#ffd666" },
            { min: 50, max: 999, label: "50-1000", color: "#1677ff" },
            { min: 1, max: 49, label: "低于50", color: "#b7dcff" },
            { value: 0, label: "无访问", color: "#eef3f9" },
          ],
          textStyle: {
            color: "#607089",
            fontSize: 12,
          },
        },
        geo: {
          map: MAP_NAME,
          roam: true,
          layoutCenter: ["54%", "50%"],
          layoutSize: "134%",
          zoom: 1.32,
          top: 0,
          bottom: 0,
          itemStyle: {
            borderColor: "#ffffff",
            borderWidth: 1,
            areaColor: "#eef3f9",
          },
          emphasis: {
            disabled: true,
          },
        },
        series: [
          {
            type: "map",
            geoIndex: 0,
            selectedMode: false,
            emphasis: {
              disabled: true,
              label: {
                show: false,
                color: "#1f2d3d",
                fontSize: 12,
              },
            },
            label: {
              show: true,
              color: "#1f2d3d",
              fontSize: 11,
              lineHeight: 13,
              formatter: (params) => {
                const value = Number(params.data?.value || 0);
                return value > 0 ? `${params.name}\n${value}` : "";
              },
            },
            itemStyle: {
              borderColor: "#ffffff",
              borderWidth: 1,
              areaColor: "#eef3f9",
            },
            data: this.rows.map((item) => ({
              name: this.mapCountryName(item.country),
              value: Number(item.sessions || 0),
              active_users: Number(item.active_users || 0),
              raw_country: item.country,
            })),
          },
          {
            type: "lines",
            coordinateSystem: "geo",
            zlevel: 2,
            silent: true,
            symbol: ["none", "none"],
            lineStyle: {
              width: 1,
              opacity: 0.72,
              curveness: 0.12,
            },
            data: this.leaderLineRows(),
          },
          {
            type: "scatter",
            coordinateSystem: "geo",
            zlevel: 3,
            symbol: "circle",
            symbolSize: 5,
            label: {
              show: false,
            },
            emphasis: {
              scale: 1.2,
            },
            data: this.smallCountryPointRows(),
          },
          {
            type: "scatter",
            coordinateSystem: "geo",
            zlevel: 2,
            symbol: "circle",
            symbolSize: (value) => {
              const sessions = Number(value[2] || 0);
              return Math.max(9, Math.min(22, 8 + Math.sqrt(sessions) * 1.8));
            },
            label: {
              show: true,
              formatter: (params) => `${params.name}\n${params.data.sessions}`,
              position: "right",
              color: "#1f2d3d",
              fontSize: 11,
              lineHeight: 13,
              backgroundColor: "rgba(255,255,255,0.78)",
              borderRadius: 4,
              padding: [2, 4],
            },
            emphasis: {
              scale: 1.15,
              label: {
                show: true,
              },
            },
            data: this.normalMarkerRows(),
          },
          {
            type: "scatter",
            coordinateSystem: "geo",
            zlevel: 3,
            symbol: "pin",
            symbolSize: 1,
            tooltip: {
              show: false,
            },
            label: {
              show: true,
              formatter: (params) => `${params.name}\n${params.data.sessions}`,
              position: "inside",
              color: "#1f2d3d",
              fontSize: 11,
              lineHeight: 13,
              backgroundColor: "rgba(255,255,255,0.88)",
              borderColor: "rgba(96,112,137,0.18)",
              borderWidth: 1,
              borderRadius: 4,
              padding: [3, 6],
            },
            data: this.smallCountryLabelRows(),
          },
        ],
      });
      this.$nextTick(this.resizeChart);
    },
  },
};
</script>

<style lang="scss" scoped>
.google-country-map {
  position: relative;
  min-height: 300px;

  &__canvas {
    width: 100%;
  }

  &__empty {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    font-size: 13px;
    pointer-events: none;
  }
}
</style>
